import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { intersectionWith, isUndefined, keys } from 'lodash'
import { Button, Card, CardContent, Grid, Icon } from '@mui/material'
import StepperDialog from '../../../../../components/modal/StepperDialog'
import { ButtonMUI } from '../../../../../components/styled/Buttons'
import Select from '../../../../../components/forms/Select'
import Input from '../../../../../components/forms/Input'
import NumberField from '../../../../../components/forms/NumberField'
import { ParameterFilterField } from '../../../../../components/datatable/SimpleSelectionTable'
import { ALIGN, CardTable } from '../../../../../components/datatable/NewTable'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import { SHOW_SANDRE_CODE } from '../../../../../account/constants/AccountSettingsConstants'
import useAccountSetting from '../../../../../utils/customHook/useAccountSetting'
import useApplicationSetting from '../../../../../utils/customHook/useApplicationSetting'
import useLocalStorage from '../../../../../utils/customHook/useLocalStorage'
import useUpdateEffect from '../../../../../utils/customHook/useUpdateEffect'
import useSandreList from '../../../../../utils/customHook/useSandreList'
import { filterActiveElements } from '../../../../../utils/ListUtil'
import { getLabel } from '../../../../../utils/StoreUtils'
import { getSandreLabel, normalizeString } from '../../../../../utils/StringUtil'
import { skyBlue, whiteColor } from '../../../../../utils/constants/ColorTheme'
import { ParameterStep } from '../stepper/Step'
import OperationAction from '../../actions/OperationAction'

const STEP_PARAMETER = 0
const STEP_CONDITION = 1

const CONDITION_FIELDS_STORAGE = 'OPERATION_ENVIRONMENTAL_CONDITION_FIELDS'

const MeasureField = ({
    parameter,
    measure,
    onChange = () => { },
}) => {
    const sandreList = useSandreList(`PARAMETRE.${parameter}`)

    return sandreList.length ? (
        <Select
            label={i18n.measure}
            options={sandreList}
            value={measure}
            onChange={onChange}
            clearFunction
        />
    ) : (
        <NumberField
            title={i18n.measure}
            value={measure}
            floatValue
            onChange={onChange}
        />
    )
}

MeasureField.propTypes = {
    parameter: PropTypes.string,
    measure: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
}

const ConditionFields = ({
    parameter,
    conditionFields = {},
    setConditionFields = () => { },
    onValidate = () => { },
}) => {
    const {
        methods,
        units,
        statusList,
        qualifications,
    } = useSelector(store => ({
        methods: store.MethodReducer.methods,
        units: store.UnitReducer.units,
        statusList: store.QualityReducer.status,
        qualifications: store.QualityReducer.qualifications,
    }), shallowEqual)

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                    <Grid size={3}>
                        <MeasureField
                            parameter={parameter}
                            measure={conditionFields.measure}
                            onChange={measure => setConditionFields(prevFields => ({ ...prevFields, measure }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.unit}
                            options={filterActiveElements(units)}
                            value={conditionFields.unitCode}
                            onChange={unitCode => setConditionFields(prevFields => ({ ...prevFields, unitCode }))}
                            clearFunction
                            keyLabel='symbolWithName'
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.method}
                            options={filterActiveElements(methods)}
                            value={conditionFields.method}
                            nullLabel='&nbsp;'
                            clearFunction
                            onChange={method => setConditionFields(prevFields => ({ ...prevFields, method: isUndefined(method) ? undefined : `${method}` }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.status}
                            options={statusList}
                            value={conditionFields.status}
                            keyLabel='name'
                            keyValue='code'
                            returnNull
                            onChange={status => setConditionFields(prevFields => ({ ...prevFields, status }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.qualification}
                            options={qualifications}
                            value={conditionFields.qualification}
                            keyLabel='name'
                            keyValue='code'
                            returnNull
                            onChange={qualification => setConditionFields(prevFields => ({ ...prevFields, qualification }))}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.comment}
                            value={conditionFields.comment}
                            onChange={comment => setConditionFields(prevFields => ({ ...prevFields, comment }))}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Button
                            onClick={onValidate}
                            variant='contained'
                            color='primary'
                            fullWidth
                        >
                            {i18n.validate}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

ConditionFields.propTypes = {
    parameter: PropTypes.string,
    conditionFields: PropTypes.shape({
        measure: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unitCode: PropTypes.string,
        method: PropTypes.string,
        status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        qualification: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        comment: PropTypes.string,
    }),
    setConditionFields: PropTypes.func,
    onValidate: PropTypes.func,
}

const ConditionStep = ({
    conditions = [],
    setConditions = () => { },
}) => {
    const {
        parameters,
        methods,
        units,
        statusList,
        qualifications,
        sandreCodes,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        methods: store.MethodReducer.methods,
        units: store.UnitReducer.units,
        statusList: store.QualityReducer.status,
        qualifications: store.QualityReducer.qualifications,
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [selectedConditions, setSelectedConditions] = useState({}) // key: parameter code, value: boolean
    const [filter, setFilter] = useState({})
    const [conditionFields, setConditionFields] = useLocalStorage(CONDITION_FIELDS_STORAGE, {})

    const selectedParameters = keys(selectedConditions).filter(key => selectedConditions[key])
    const isSomeConditionSelected = selectedParameters.length > 0
    // la liste SANDRE dépend du paramètre : on ne la propose que si un seul paramètre est sélectionné
    const [singleSelectedParameter] = selectedParameters.length === 1 ? selectedParameters : []

    const formattedConditions = useMemo(() => {
        return conditions.map(c => {
            const isSelected = selectedConditions[c.parameter]
            const parameter = getLabel(parameters, c.parameter, showSandreCode ? 'labelWithCode' : 'name')
            const unit = getLabel(units, c.unitCode, showSandreCode ? 'symbolWithCode' : 'symbol')
            const method = getLabel(methods, c.method)
            const status = getLabel(statusList, c.status)
            const qualification = getLabel(qualifications, c.qualification)
            const measure = getSandreLabel(sandreCodes, `PARAMETRE.${c.parameter}`, c.measure) || c.measure

            return {
                parameter,
                measure,
                unit,
                method,
                status,
                qualification,
                comment: c.comment,
                check: (<Icon>{isSelected ? 'check_box' : 'check_box_outline_blank'}</Icon>),
                colorLine: isSelected ? skyBlue : whiteColor,
                code: c.parameter,
                searchValue: [parameter, c.parameter, `${measure ?? ''}`, unit, method, status, qualification, c.comment].map((label = '') => normalizeString(label)),
            }
        })
    }, [conditions, methods, parameters, qualifications, sandreCodes, selectedConditions, showSandreCode, statusList, units])

    const filteredConditions = useMemo(() => {
        const { selection = '-1', listParam, searchValue } = filter
        const filterSelection = selection !== '-1' ? intersectionWith(formattedConditions, listParam, (c, code) => c.code === code) : formattedConditions
        const searchValueFormat = normalizeString(searchValue)
        return searchValue ? filterSelection.filter(c => c.searchValue.some(v => v.includes(searchValueFormat))) : filterSelection
    }, [filter, formattedConditions])

    const isAllConditionSelected = filteredConditions.every(c => selectedConditions[c.code])

    const onSelectAll = () => {
        setSelectedConditions(prevSelected => filteredConditions.reduce((acc, c) => {
            acc[c.code] = !isAllConditionSelected
            return acc
        }, { ...prevSelected }))
    }

    const actions = filteredConditions.length ? [{
        icon: isAllConditionSelected ? 'check_box' : 'check_box_outline_blank',
        tooltip: i18n.edit,
        onClick: onSelectAll,
    }] : []

    return (
        <Grid container justifyContent='center' rowSpacing={1}>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <ParameterFilterField
                            filter={filter}
                            setFilter={setFilter}
                        />
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={i18n.environmentalConditions}
                    actions={actions}

                    rows={filteredConditions}
                    headers={['parameter', 'measure', 'unit', 'method', 'status', 'qualification', 'comment', { key: 'check', value: '', sticky: ALIGN.RIGHT }]}

                    maxHeight={isSomeConditionSelected ? '36vh' : '45vh'}
                    onClickRow={({ code }) => setSelectedConditions(prevSelected => ({ ...prevSelected, [code]: !prevSelected[code] }))}
                    rowsPerPageOptions={nbPerPageLabel}
                />
            </Grid>
            {isSomeConditionSelected && (
                <Grid size={12}>
                    <ConditionFields
                        parameter={singleSelectedParameter}
                        conditionFields={conditionFields}
                        setConditionFields={setConditionFields}
                        onValidate={() => {
                            setConditions(prevConditions => prevConditions.map(c => selectedConditions[c.parameter] ? { parameter: c.parameter, ...conditionFields } : c))
                            setSelectedConditions({})
                        }}
                    />
                </Grid>
            )}
        </Grid>
    )
}

ConditionStep.propTypes = {
    conditions: PropTypes.arrayOf(PropTypes.shape({
        parameter: PropTypes.string,
    })),
    setConditions: PropTypes.func,
}

const EnvironmentalConditionStepper = ({
    isOpen = false,
    onClose = () => { },
    operation = {},
}) => {
    const dispatch = useDispatch()

    const [conditions, setConditions] = useState([])

    useUpdateEffect(() => {
        setConditions([])
    }, [isOpen])

    const onSave = () => {
        if (!conditions.length) {
            onClose()
            return
        }
        const date = moment().valueOf()
        const newConditions = conditions.map(({ parameter, method, ...fields }) => ({
            ...fields,
            parameterCode: parseInt(parameter),
            method: parseInt(method) || undefined,
            date,
        }))
        dispatch(OperationAction.createEnvironmentalConditions(operation.qualitometer, operation.id, newConditions)).finally(onClose)
    }

    return (
        <StepperDialog
            steps={[
                {
                    label: `${i18n.selection} / ${i18n.parameter}`,
                    constant: STEP_PARAMETER,
                },
                {
                    label: i18n.environmentalConditions,
                    constant: STEP_CONDITION,
                },
            ]}
            open={isOpen}
            title={i18n.newEnvironmentalCondition}
            closeDialog={onClose}
            renderSaveButton={step => step === STEP_CONDITION && (
                <ButtonMUI
                    onClick={onSave}
                    variant='contained'
                >
                    {i18n.register}
                </ButtonMUI>
            )}
            contentStyle={{ padding: '0 12' }}
        >
            {step => (
                <>
                    {step === STEP_PARAMETER && (
                        <ParameterStep
                            title={i18n.parametersSelection}
                            analysis={conditions}
                            setAnalysis={setConditions}
                        />
                    )}
                    {step === STEP_CONDITION && (
                        <ConditionStep
                            conditions={conditions}
                            setConditions={setConditions}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

EnvironmentalConditionStepper.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    operation: PropTypes.shape({
        id: PropTypes.number,
        qualitometer: PropTypes.number,
    }),
}

export default EnvironmentalConditionStepper
